import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useActiveProject } from '../../../core/contexts/ProjectContext';
import { extractErrorMessage, extractJobErrorMessage, resources } from '../../../core/services';
import { useToast } from '../../../shared/components/Toast/ToastContext';
import type { Project, ProjectTypeProfile } from '../../../core/types/data-models';
import type { OnboardingDraft } from './useOnboardingDraft';
import { normalizeUrl } from './SourcesStep';

export type CreateProjectStatus = 'idle' | 'creating' | 'starting';

/** One line of prose for the project card: the first non-heading line of the description. */
function summarize(description: string): string | undefined {
  const line = description
    .split('\n')
    .filter((l) => !l.trimStart().startsWith('#'))
    .map((l) => l.replace(/^[>*\-\s]+/, '').trim())
    .find((l) => l.length > 0);
  return line ? line.slice(0, 200) : undefined;
}

function toCreatePayload(draft: OnboardingDraft, profile: ProjectTypeProfile): Partial<Project> {
  const url = (value: string) => (value.trim() ? normalizeUrl(value) : null);
  const websiteUrl = url(draft.websiteUrl);
  // The website first, then any further pages, without repeats.
  const websiteUrls = [...(websiteUrl ? [websiteUrl] : []), ...draft.otherUrls.map(normalizeUrl).filter(Boolean)].filter(
    (value, i, all) => all.indexOf(value) === i,
  );
  return {
    name: draft.name.trim(),
    projectType: profile.type,
    description: summarize(draft.description),
    // The full story (or the investor's notes) lives in userPrompt; description stays the short card summary.
    userPrompt: draft.description.trim() || null,
    websiteUrls,
    githubUrl: url(draft.githubUrl),
    docsUrl: url(draft.docsUrl),
    linkedinUrl: url(draft.linkedinUrl),
    // The stock, in its own fields: the backend turns the symbol into the
    // Yahoo Finance one (7203 on Tokyo → 7203.T) that links the project to
    // the stock's quotes and prices.
    ...(profile.type === 'STOCK_VALIDATION'
      ? { symbol: draft.symbol.trim().toUpperCase(), exchange: draft.exchange.trim() }
      : {}),
  };
}

/**
 * Kicks off the validation the way the profile says: a business project
 * has its sources analyzed into a company brief first (the analysis chains
 * the first stage itself); an idea project generates the profile's kickoff
 * document type straight away, by the same generate-document call the
 * dashboard makes (the GENERATE_DOCUMENT job is the document-entity job).
 * The idea is the prompt; the website, if any, is appended so the analysis
 * knows it exists.
 */
async function startProject(project: Project, draft: OnboardingDraft, profile: ProjectTypeProfile) {
  if (profile.start === 'analyze-sources') {
    await resources.analyzeProjectSources(project.id);
    return;
  }

  // The catalog is admin-managed, so resolve the type by its label rather
  // than assuming label === type (same as ProjectDetail).
  const catalog = await resources.documentTypes.list({ pageSize: 500, sort: 'order' });
  const docType = catalog.data.find((dt) => dt.label === profile.kickoffDocumentType);
  if (!docType) throw new Error(`Document type "${profile.kickoffDocumentType}" is not available.`);

  const story = draft.description.trim();
  const websiteUrl = normalizeUrl(draft.websiteUrl);
  await resources.generateDocument(project.id, {
    documentType: docType.type,
    generateUserPrompt: websiteUrl ? `${story}\n\nExisting website: ${websiteUrl}` : story,
    // A validation stage is read on the validation page, not on a
    // dashboard: skip the follow-up job that builds dashboard cards (same as
    // the validation page does for the later stages). Entities still run.
    skipDashboard: profile.validationCategory != null && docType.categoryType === profile.validationCategory,
  });
}

/**
 * One submit path for every onboarding wizard:
 * projects.create → start the validation → setActiveProject
 * → navigate to the project's validation page.
 *
 * A create failure keeps the user on the wizard with the error. A start
 * failure is not fatal: the project already exists and the validation page
 * offers the same start again, so the user lands there with a warning.
 */
export function useCreateAndStartProject(
  profile: ProjectTypeProfile,
  options: { /** Called once the project exists, right before leaving the wizard. */ onCreated?: () => void } = {},
) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setActiveProject } = useActiveProject();
  const toast = useToast();
  const [status, setStatus] = useState<CreateProjectStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const submit = async (draft: OnboardingDraft) => {
    setError(null);
    setStatus('creating');

    let project: Project;
    try {
      project = await resources.projects.create(toCreatePayload(draft, profile));
    } catch (e) {
      setError(extractErrorMessage(e));
      setStatus('idle');
      return;
    }
    queryClient.invalidateQueries({ queryKey: ['projects'] });
    queryClient.invalidateQueries({ queryKey: ['my-projects'] });
    setActiveProject({ id: project.id, name: project.name, description: project.description });

    setStatus('starting');
    try {
      await startProject(project, draft, profile);
    } catch (e) {
      toast({
        title: 'Project created, but the validation did not start',
        message: `${extractJobErrorMessage(e)} You can start it from the validation page.`,
        severity: 'warning',
      });
    }

    setStatus('idle');
    options.onCreated?.();
    navigate(`/projects/${project.id}/validation`, { replace: true });
  };

  return { submit, status, error, clearError: () => setError(null) };
}
